import { css, Theme, Interpolation } from '@emotion/react';
import { Call } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { useTranslation, i18next } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { TableLoadingSkeleton, Text } from '@frontend/design-system';
import { useCallIntelDefaultsShallowStore } from '../hooks';
import { callStatusUtils } from '../utils';
import { Accept } from './accept';
import { CallIntelTooltip } from './call-intel-tooltip';
import { CallIntelChip } from './chip';
import TaskChip from './chip/task-chip';
import { ColumnHeaderInfoTip } from './column-header-info-tip';
import { EmptyCell } from './empty-cell';

type CallRecordStatus = 'abandoned' | 'missed';
type ColumnCellProps = {
  direction?: string;
  callInsights: Call;
  callRecordStatus?: CallRecordStatus;
};
type ColumnHeaderProps = { showCallIntelPrefix?: boolean };
type CommonCellRendererOverrides = {
  outboundNoInsightsTooltip?: string;
};

const getCommonCellRenderer = ({
  callInsights,
  direction,
  callRecordStatus,
  overrides,
}: ColumnCellProps & { overrides?: CommonCellRendererOverrides }) => {
  if (direction === 'outbound' && !callInsights)
    return (
      <EmptyCell tooltipText={overrides?.outboundNoInsightsTooltip ?? i18next.t('Outbound call', { ns: 'calls' })} />
    );

  if (callRecordStatus === 'missed')
    return <EmptyCell tooltipText={i18next.t('Missed call', { ns: 'call-intel-core' })} />;

  if (callRecordStatus === 'abandoned')
    return <EmptyCell tooltipText={i18next.t('Abandoned call', { ns: 'call-intel-core' })} />;

  if (!callInsights) return <EmptyCell showTooltip={false} />;

  const { isFailedCall, isSkippedCall, isProcessingCall } = callStatusUtils.getCallFlags(
    callInsights as CallIntelTypes.Call
  ); // TODO:remove cast till we migrate to new schema

  if (isProcessingCall) return <TableLoadingSkeleton />;

  if (isFailedCall)
    return <EmptyCell tooltipText={i18next.t('Failed Call Intelligence analysis', { ns: 'call-intel-core' })} />;

  if (isSkippedCall)
    return <EmptyCell tooltipText={i18next.t('Skipped Call Intelligence analysis', { ns: 'call-intel-core' })} />;

  return null;
};

const CallIntelCellContent: React.FC<{
  children: React.ReactNode;
  editedText?: string;
  isEdited?: boolean;
  customStyles?: Interpolation<Theme>;
}> = ({ children, editedText, isEdited, customStyles }) => (
  <div css={customStyles || styles.fieldWrapper}>
    {children}
    {isEdited && editedText && (
      <Text size='small' color='subdued' css={styles.editedText}>
        {editedText}
      </Text>
    )}
  </div>
);

export const ContactTypeColumn = {
  Header: ({ showCallIntelPrefix }: ColumnHeaderProps) => {
    const { t } = useTranslation('call-intel-core');
    return (
      <ColumnHeaderInfoTip
        columnTitle={t('Contact Type')}
        infoTip={<CallIntelTooltip noCta tip='contactType' showCallIntelPrefix={showCallIntelPrefix} />}
        showAiIcon
      />
    );
  },
  Cell: ({ callInsights, callRecordStatus, direction }: ColumnCellProps) => {
    const { t } = useTranslation('call-intel-core');
    const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

    const renderer = getCommonCellRenderer({ callInsights, callRecordStatus, direction });
    if (renderer) return renderer;

    const { contactType, isFieldEdited } = callInsights;
    if (!contactType) return <EmptyCell showTooltip={false} />;

    return (
      <CallIntelCellContent
        customStyles={styles.chips}
        isEdited={isFieldEdited?.isContactTypeEdited}
        editedText={t('Edited')}
      >
        <CallIntelChip
          chipVariants={chipVariants}
          dataKey={contactType}
          label={dataLabels.contactType?.[contactType] ?? ''}
        />
      </CallIntelCellContent>
    );
  },
};

export const SchedulingOutcomeColumn = {
  Header: ({ showCallIntelPrefix }: ColumnHeaderProps) => {
    const { t } = useTranslation('call-intel-core');
    return (
      <ColumnHeaderInfoTip
        columnTitle={t('Scheduling Outcome')}
        infoTip={<CallIntelTooltip noCta tip='schedulingOutcome' showCallIntelPrefix={showCallIntelPrefix} />}
        showAiIcon
      />
    );
  },
  Cell: ({ callInsights, callRecordStatus, direction }: ColumnCellProps) => {
    const { t } = useTranslation('call-intel-core');

    const renderer = getCommonCellRenderer({ callInsights, callRecordStatus, direction });
    if (renderer) return renderer;

    const { callSchedulingOutcome, callSchedulingOpportunity, isFieldEdited } = callInsights;

    if (callSchedulingOutcome === null) {
      // Scheduling outcome can only be null in case of non-patient calls,so not checking for contact type
      return (
        <EmptyCell
          edited={isFieldEdited?.isSchedulingOutcomeEdited}
          tooltipText={t('Scheduling Outcome isn’t analyzed for non-patient calls.')}
        />
      );
    }

    return (
      <CallIntelCellContent isEdited={isFieldEdited?.isSchedulingOutcomeEdited} editedText={t('Edited')}>
        {callSchedulingOpportunity ? (
          <Accept accepted={callSchedulingOutcome} />
        ) : (
          <EmptyCell tooltipText={t('No Scheduling Outcome due to no Opportunity.')}>{t('N/A')}</EmptyCell>
        )}
      </CallIntelCellContent>
    );
  },
};

export const SentimentColumn = {
  Header: ({ showCallIntelPrefix }: ColumnHeaderProps) => {
    const { t } = useTranslation('call-intel-core');

    return (
      <ColumnHeaderInfoTip
        columnTitle={t('Customer Sentiment')}
        infoTip={<CallIntelTooltip noCta tip='sentiments' showCallIntelPrefix={showCallIntelPrefix} />}
        showAiIcon
      />
    );
  },
  Cell: ({ callInsights, callRecordStatus, direction }: ColumnCellProps) => {
    const { t } = useTranslation('call-intel-core');
    const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

    const renderer = getCommonCellRenderer({ callInsights, callRecordStatus, direction });
    if (renderer) return renderer;

    const { callSentiment, isFieldEdited } = callInsights;

    if (callSentiment?.sentiment) {
      return (
        <CallIntelCellContent isEdited={isFieldEdited?.isSentimentEdited} editedText={t('Edited')}>
          <CallIntelChip
            as='chip'
            chipVariants={chipVariants}
            dataKey={callSentiment.sentiment}
            label={dataLabels.sentiments?.[callSentiment.sentiment] || callSentiment.sentiment}
            key={callSentiment.sentiment}
          />
        </CallIntelCellContent>
      );
    }

    // Sentiment can only be null in case of non-patient calls,so not checking for contact type
    return (
      <EmptyCell
        edited={isFieldEdited?.isSentimentEdited}
        tooltipText={t('Customer Sentiment isn’t analyzed for non-patient calls.')}
      />
    );
  },
};

export const CategoryColumn = {
  Header: ({ showCallIntelPrefix }: ColumnHeaderProps) => {
    const { t } = useTranslation('call-intel-core');
    return (
      <ColumnHeaderInfoTip
        columnTitle={t('Category')}
        infoTip={
          <CallIntelTooltip
            customTipTitle={t('Category')}
            noCta
            tip='callsByCategory'
            showCallIntelPrefix={showCallIntelPrefix}
          />
        }
        showAiIcon
      />
    );
  },
  Cell: ({ callInsights, callRecordStatus, direction }: ColumnCellProps) => {
    const { t } = useTranslation('call-intel-core');
    const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

    const renderer = getCommonCellRenderer({ callInsights, callRecordStatus, direction });
    if (renderer) return renderer;

    const { categories, isFieldEdited } = callInsights;

    if (!categories) return <EmptyCell showTooltip={false} />;

    return categories?.length ? (
      <CallIntelCellContent
        isEdited={isFieldEdited?.isCategoryEdited}
        editedText={t('Edited')}
        customStyles={styles.chips}
      >
        {categories.map((category: string) =>
          category.trim() ? (
            <CallIntelChip
              as='category'
              chipVariants={chipVariants}
              dataKey={category}
              label={dataLabels.categories?.[category] || category}
              key={category}
            />
          ) : null
        )}
      </CallIntelCellContent>
    ) : (
      <CallIntelCellContent isEdited={isFieldEdited?.isCategoryEdited}>
        <EmptyCell showTooltip={false} />
      </CallIntelCellContent>
    );
  },
};

export const AppointmentTypeColumn = {
  Header: ({ showCallIntelPrefix }: ColumnHeaderProps) => {
    const { t } = useTranslation('call-intel-core');
    return (
      <ColumnHeaderInfoTip
        columnTitle={t('Appointment Type')}
        infoTip={
          <CallIntelTooltip
            customTipTitle={t('Appointment Type')}
            noCta
            tip='callsByAppointmentType'
            showCallIntelPrefix={showCallIntelPrefix}
          />
        }
        showAiIcon
      />
    );
  },
  Cell: ({ callInsights, callRecordStatus, direction }: ColumnCellProps) => {
    const { t } = useTranslation('call-intel-core');
    const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

    const renderer = getCommonCellRenderer({ callInsights, callRecordStatus, direction });
    if (renderer) return renderer;

    const { appointmentTypes, contactType, isFieldEdited } = callInsights;

    return appointmentTypes?.length ? (
      <CallIntelCellContent
        isEdited={isFieldEdited?.isAppointmentTypeEdited}
        editedText={t('Edited')}
        customStyles={styles.chips}
      >
        {appointmentTypes.map((appointmentType: string) =>
          appointmentType.trim() ? (
            <CallIntelChip
              as='category'
              chipVariants={chipVariants}
              dataKey={appointmentType}
              label={dataLabels.appointmentTypes?.[appointmentType] || appointmentType}
              key={appointmentType}
            />
          ) : null
        )}
      </CallIntelCellContent>
    ) : (
      <CallIntelCellContent isEdited={isFieldEdited?.isAppointmentTypeEdited}>
        <EmptyCell
          tooltipText={
            contactType !== CallIntelTypes.ContactTypeEnum.CONTACT_NOT_A_PATIENT
              ? t('No Appointment Type discussed on the call.')
              : t('Appointment Type isn’t analyzed for non-patient calls.')
          }
        />
      </CallIntelCellContent>
    );
  },
};

export const TaskColumn = {
  Header: ({ showCallIntelPrefix }: ColumnHeaderProps) => {
    const { t } = useTranslation('call-intel-core');
    return (
      <ColumnHeaderInfoTip
        columnTitle={t('Tasks')}
        infoTip={<CallIntelTooltip noCta tip='tasks' showCallIntelPrefix={showCallIntelPrefix} />}
      />
    );
  },
  Cell: ({
    callInsights,
    callRecordStatus,
    direction,
    taskCount,
    onClick,
  }: ColumnCellProps & { taskCount?: number; onClick?: () => void }) => {
    const { t } = useTranslation('call-intel-core');

    const renderer = getCommonCellRenderer({
      callInsights,
      callRecordStatus,
      direction,
      overrides: { outboundNoInsightsTooltip: t('No Tasks') },
    });
    if (renderer) return renderer;

    return (
      <>
        {taskCount ? <TaskChip taskCount={taskCount} onClick={onClick} /> : <EmptyCell tooltipText={t('No Tasks')} />}
      </>
    );
  },
};

const styles = {
  chips: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
    align-items: center;
  `,

  editedText: css`
    font-style: italic;
  `,

  fieldWrapper: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
    align-items: center;
    width: 100%;
  `,
};
